import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Mic, MicOff, Search, X, RotateCcw, Loader2, Square } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const SR_LANG = { en: "en-JM", jam: "en-JM", es: "es-ES", fr: "fr-FR", pt: "pt-BR", de: "de-DE", zh: "zh-CN", "zh-Hans": "zh-CN", ja: "ja-JP", ar: "ar-SA", he: "he-IL", hi: "hi-IN", nl: "nl-NL", it: "it-IT", ko: "ko-KR", ru: "ru-RU" };
const hasSR = () => !!(window.SpeechRecognition || window.webkitSpeechRecognition);
const canRecord = () => !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);

export function VoiceSearch({ compact = false }) {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | listening | transcribing | routing | ready | error | denied | unsupported
  const [text, setText] = useState("");
  const [interim, setInterim] = useState("");
  const [route, setRoute] = useState(null);
  const [err, setErr] = useState("");
  const recRef = useRef(null); const mediaRef = useRef(null); const chunksRef = useRef([]); const timerRef = useRef(null);

  const stopAll = () => {
    try { recRef.current?.stop(); } catch {}
    try { if (mediaRef.current?.state === "recording") mediaRef.current.stop(); } catch {}
    mediaRef.current?.stream?.getTracks().forEach((t) => t.stop());
    clearTimeout(timerRef.current);
  };
  useEffect(() => () => stopAll(), []);

  const interpret = async (t) => {
    const q = (t || "").trim();
    if (!q) { setPhase("error"); setErr("I didn't catch that — tap the mic and try again, or type your search."); return; }
    setPhase("routing");
    try {
      const { data } = await api.post("/voice/route", { text: q, lang });
      setRoute(data);
      if (data.intent === "empty") { setPhase("error"); setErr(data.label); return; }
      setPhase("ready");
      if (data.intent !== "search" || !data.options) go(data);
    } catch { setPhase("ready"); setRoute({ intent: "search", path: `/search?q=${encodeURIComponent(q)}`, label: q }); go({ intent: "search", path: `/search?q=${encodeURIComponent(q)}` }); }
  };

  const go = (r) => {
    const target = r || route; if (!target) return;
    setOpen(false); stopAll();
    if (target.intent === "assistant") { window.dispatchEvent(new CustomEvent("shopai:open")); return; }
    if (target.path) navigate(target.path);
  };

  const startBrowser = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR();
    r.lang = SR_LANG[lang] || SR_LANG[(lang || "en").split("-")[0]] || "en-JM"; r.interimResults = true; r.maxAlternatives = 1; r.continuous = false;
    let final = "";
    r.onstart = () => { setPhase("listening"); setInterim(""); };
    r.onresult = (e) => { let i = ""; for (let k = e.resultIndex; k < e.results.length; k++) { const s = e.results[k][0].transcript; if (e.results[k].isFinal) final += s; else i += s; } setInterim(i); if (final) setText(final.trim()); };
    r.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") { setPhase("denied"); return; }
      if (e.error === "no-speech") { setPhase("error"); setErr("No speech detected — please speak after tapping the mic."); return; }
      if (e.error === "network" && canRecord()) { startRecorder(); return; }
      setPhase("error"); setErr("Voice recognition hit a problem — try again or type your search.");
    };
    r.onend = () => { setInterim(""); if (final.trim()) interpret(final); else setPhase((p) => (p === "listening" ? "error" : p)); if (!final.trim()) setErr((e) => e || "No speech detected — please try again."); };
    recRef.current = r;
    try { r.start(); } catch { setPhase("error"); setErr("Microphone is busy — try again."); }
  };

  const startRecorder = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setPhase("transcribing");
        const fd = new FormData(); fd.append("audio", blob, `speech.${(mr.mimeType || "audio/webm").includes("mp4") ? "mp4" : "webm"}`); if (lang) fd.append("lang", lang);
        try { const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 }); setText(data.text); interpret(data.text); }
        catch (e) { setPhase("error"); setErr(e?.response?.data?.detail || "Could not transcribe — please try again or type your search."); }
      };
      mediaRef.current = mr; mr.start(); setPhase("listening");
      timerRef.current = setTimeout(() => { if (mr.state === "recording") mr.stop(); }, 12000);
    } catch (e) {
      if (e?.name === "NotAllowedError" || e?.name === "SecurityError") setPhase("denied");
      else { setPhase("unsupported"); }
    }
  };

  const start = () => {
    setText(""); setInterim(""); setRoute(null); setErr(""); setOpen(true);
    if (!window.isSecureContext) { setPhase("unsupported"); setErr("Microphone needs a secure (https) connection."); return; }
    if (hasSR()) startBrowser(); else if (canRecord()) startRecorder(); else setPhase("unsupported");
  };
  const stopListening = () => { stopAll(); if (phase === "listening" && !hasSR()) return; };
  const close = () => { stopAll(); setOpen(false); setPhase("idle"); };
  const typed = (e) => { e.preventDefault(); interpret(text); };

  return (
    <>
      <button type="button" onClick={start} data-testid="voice-search-btn" aria-label="Smart AI Voice Search — tap to speak and search"
        className={`group relative flex items-center gap-2 rounded-full border border-[#E11B22]/30 bg-white pl-1 pr-3 py-1 hover:border-[#E11B22] hover:shadow-[0_0_0_4px_rgba(225,27,34,0.12)] transition-all shrink-0 ${compact ? "pr-1" : ""}`}>
        <span className="relative grid h-8 w-8 place-items-center rounded-full bg-[#E11B22] text-white shadow-md">
          <Mic size={16} />
          <span className="absolute inset-0 rounded-full bg-[#E11B22]/40 animate-ping opacity-0 group-hover:opacity-100" />
        </span>
        {!compact && <span className="leading-tight text-left whitespace-nowrap"><span className="block text-[11px] font-bold text-ink">Smart AI Voice Search</span><span className="block text-[9px] uppercase tracking-wide text-[#E11B22] font-semibold">Tap to Speak & Search</span></span>}
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" data-testid="voice-search-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
          <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl sn-fade-up">
            <button onClick={close} className="absolute right-4 top-4 text-ink/60 hover:text-ink" data-testid="voice-close-btn" aria-label="Close"><X size={20} /></button>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E11B22] font-bold">Smart AI Voice Search</div>
            <h3 className="font-clash font-bold text-xl text-ink mt-1" data-testid="voice-status">
              {phase === "listening" && "Listening… speak now"}{phase === "transcribing" && "Converting your speech…"}{phase === "routing" && "Finding the right results…"}
              {phase === "ready" && "Here's what I heard"}{phase === "error" && "Let's try that again"}{phase === "denied" && "Microphone blocked"}{phase === "unsupported" && "Voice not available here"}{phase === "idle" && "Tap the mic to speak"}
            </h3>

            <div className="my-5 grid place-items-center">
              <button onClick={phase === "listening" ? stopListening : start} data-testid="voice-mic-toggle" aria-label={phase === "listening" ? "Stop listening" : "Start listening"}
                className={`relative grid h-20 w-20 place-items-center rounded-full text-white shadow-xl transition-transform ${phase === "listening" ? "bg-[#E11B22] scale-110" : "bg-ink hover:bg-[#E11B22]"}`}>
                {phase === "listening" && <><span className="absolute inset-0 rounded-full bg-[#E11B22]/40 animate-ping" /><span className="absolute -inset-3 rounded-full border-2 border-[#E11B22]/30 animate-pulse" /></>}
                {phase === "transcribing" || phase === "routing" ? <Loader2 size={30} className="animate-spin" /> : phase === "listening" ? <Square size={26} fill="currentColor" /> : phase === "denied" || phase === "unsupported" ? <MicOff size={30} /> : <Mic size={30} />}
              </button>
              {phase === "listening" && <div className="mt-3 flex items-center gap-1 h-5" aria-hidden>{[0, 1, 2, 3, 4].map((i) => <span key={i} className="w-1 rounded-full bg-[#E11B22] animate-pulse" style={{ height: `${8 + ((i * 7) % 14)}px`, animationDelay: `${i * 120}ms` }} />)}</div>}
            </div>

            <form onSubmit={typed} className="space-y-3">
              <div className="rounded-2xl border border-border bg-secondary/50 px-4 py-3 min-h-[52px]">
                {phase === "listening" ? <p className="text-sm text-ink" data-testid="voice-transcript-live">{text || interim || <span className="text-muted-foreground">e.g. “rebar”, “bathroom faucets”, “hotels in Montego Bay”, “pallet loader”</span>}{interim && text ? <span className="text-muted-foreground"> {interim}</span> : null}</p>
                  : <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Correct what I heard, then search" className="w-full bg-transparent text-sm outline-none" data-testid="voice-transcript-input" />}
              </div>
              {phase === "ready" && route && (
                <div className="text-xs text-muted-foreground" data-testid="voice-route-label">
                  {route.intent === "open" ? `Opening ${route.label}` : route.intent === "travel" ? route.label : route.intent === "food" ? route.label : `Searching ${route.label}`}
                  {route.options && <div className="mt-2 flex flex-wrap gap-1.5">{route.options.map((o) => <button type="button" key={o.query} onClick={() => go({ intent: "search", path: `/search?q=${encodeURIComponent(o.query)}` })} className="rounded-full border border-border px-2.5 py-1 text-xs hover:border-[#E11B22]" data-testid={`voice-option-${o.query.replace(/\s+/g, "-")}`}>{o.label}</button>)}</div>}
                </div>
              )}
              {(phase === "error" || phase === "unsupported") && <p className="text-xs text-coral" data-testid="voice-error">{err || "This browser can't capture speech. Type your search below instead."}</p>}
              {phase === "denied" && <p className="text-xs text-coral" data-testid="voice-denied">Microphone access was denied. Allow the microphone in your browser's site settings, or type your search below.</p>}
              <div className="flex gap-2">
                <button type="submit" disabled={!text.trim() || phase === "listening" || phase === "transcribing" || phase === "routing"} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-ink text-white py-2.5 text-sm font-bold disabled:opacity-40 hover:bg-[#E11B22] transition-colors" data-testid="voice-search-submit"><Search size={15} /> {phase === "ready" ? "Go" : "Search"}</button>
                {phase !== "listening" && phase !== "unsupported" && phase !== "denied" && <button type="button" onClick={start} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:border-ink" data-testid="voice-retry-btn"><RotateCcw size={14} /> Speak again</button>}
                <button type="button" onClick={close} className="rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:border-ink" data-testid="voice-cancel-btn">Cancel</button>
              </div>
            </form>
            <p className="mt-3 text-[10px] text-muted-foreground">The mic only listens after you tap it and stops when you finish. Recordings are not stored.</p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
